import { supabase } from '../lib/supabase'

/**
 * Conversation lifecycle actions for the Inbox (Phase 8.9).
 *
 * States: 'active' (default view) | 'archived' | 'spam' | 'deleted' (soft
 * delete — Trash, no permanent purge in v1). Marking spam also records the
 * sender in the tenant-scoped blocked_senders table so the server-side
 * inbound paths file future messages from them straight into spam;
 * "Not spam" unblocks the sender and restores the conversation.
 */

export type ConversationState = 'active' | 'archived' | 'spam' | 'deleted'

export type ConversationAction =
  | 'archive'
  | 'unarchive'
  | 'spam'
  | 'not_spam'
  | 'delete'
  | 'restore'

export interface ConversationForAction {
  id: string
  channel: 'whatsapp' | 'sms' | 'email'
  contact_info: string
}

/** State a conversation ends up in after an action. */
export const STATE_AFTER_ACTION: Record<ConversationAction, ConversationState> = {
  archive: 'archived',
  unarchive: 'active',
  spam: 'spam',
  not_spam: 'active',
  delete: 'deleted',
  restore: 'active',
}

/**
 * Canonical blocklist key: lowercased email, or digits-only phone with the
 * US/Canada country code on 10-digit numbers (mirrors lib/conversationState.js).
 */
export function normalizeSenderKey(channel: string, value: string): string | null {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return null
  if (channel === 'email') return raw
  const digits = raw.replace(/\D/g, '')
  if (!digits) return null
  return digits.length === 10 ? '1' + digits : digits
}

export async function setConversationState(
  conversationId: string,
  state: ConversationState
): Promise<void> {
  const { error } = await supabase
    .from('conversations')
    .update({ state, state_changed_at: new Date().toISOString() })
    .eq('id', conversationId)
  if (error) throw error
}

/** Mark spam and remember the sender so future inbound messages skip the inbox. */
export async function markConversationSpam(convo: ConversationForAction): Promise<void> {
  await setConversationState(convo.id, 'spam')
  const sender = normalizeSenderKey(convo.channel, convo.contact_info)
  if (!sender) return
  const { error } = await supabase
    .from('blocked_senders')
    .insert({ channel: convo.channel, sender, reason: 'Marked as spam from Inbox' })
  // 23505 = sender already blocked, which is fine
  if (error && error.code !== '23505') throw error
}

/** Not spam: unblock the sender and restore the conversation to the inbox. */
export async function markConversationNotSpam(convo: ConversationForAction): Promise<void> {
  const sender = normalizeSenderKey(convo.channel, convo.contact_info)
  if (sender) {
    const { error } = await supabase
      .from('blocked_senders')
      .delete()
      .eq('channel', convo.channel)
      .eq('sender', sender)
    if (error) throw error
  }
  await setConversationState(convo.id, 'active')
}

/** Run any lifecycle action against a conversation. */
export async function applyConversationAction(
  convo: ConversationForAction,
  action: ConversationAction
): Promise<ConversationState> {
  if (action === 'spam') await markConversationSpam(convo)
  else if (action === 'not_spam') await markConversationNotSpam(convo)
  else await setConversationState(convo.id, STATE_AFTER_ACTION[action])
  return STATE_AFTER_ACTION[action]
}
